import { useState } from 'react';
import { createTicketOrder, getTicketOrderById, updateTicketOrderStatus } from '../services/event.service';
import { TicketOrder } from '../types/event';
import { useToast } from '../hooks/useToast';

export const useTicketOrder = () => {
  const [order, setOrder] = useState<TicketOrder | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const { showToast } = useToast();

  const createOrder = async (orderData: Partial<TicketOrder>) => {
    try {
      setLoading(true);
      // Construction de l'objet order_data pour la RPC
      // Utilisation d'un UUID de test pour valider la structure
      const validUUID = '8b80cf4d-3d01-4bd3-b974-5f536447e681';
      const order_data = {
        user_id: orderData.user_id && /^[0-9a-fA-F\-]{36}$/.test(orderData.user_id) ? orderData.user_id : validUUID,
        amount: orderData.amount,
        currency: orderData.currency,
        status: orderData.status,
        payment_method: orderData.payment_method,
        email: orderData.email,
        phone: orderData.phone,
        attendee_name: orderData.attendee_name, // Ajout du nom du participant
        order_code: orderData.order_code
      };

      // Extraire la quantité et l'ID du ticket pour les passer séparément
      const ticket_id = orderData.ticket_id;
      const quantity = orderData.quantity || 1;

      // Journalisation pour le débogage
      console.log('Création de commande avec quantité:', quantity);
      
      const newOrder = await createTicketOrder({
        ...order_data,
        ticket_id,
        quantity
      });
      setOrder(newOrder);
      setError(null);
      showToast('Commande créée avec succès', 'success');
      return newOrder;
    } catch (err: any) {
      setError(err);
      // Gestion des erreurs spécifiques
      if (err.message && err.message.includes('tickets disponibles')) {
        showToast(err.message, 'error');
      } else if (err.code === '23505') {
        showToast('Cette commande existe déjà', 'error');
      } else if (err.code === '23503') {
        showToast('Le ticket sélectionné n\'existe pas ou n\'est plus disponible', 'error');
      } else {
        showToast('Erreur lors de la création de la commande', 'error');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getOrder = async (id: string) => {
    try {
      setLoading(true);
      const orderData = await getTicketOrderById(id);
      setOrder(orderData);
      setError(null);
      return orderData;
    } catch (err: any) {
      setError(err);
      showToast('Erreur lors du chargement de la commande', 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (id: string, status: string, paymentId?: string) => {
    try {
      setLoading(true);
      const updatedOrder = await updateTicketOrderStatus(id, status, paymentId);
      setOrder(updatedOrder);
      setError(null);
      return updatedOrder;
    } catch (err: any) {
      setError(err);
      showToast('Erreur lors de la mise à jour du statut de la commande', 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { order, loading, error, createOrder, getOrder, updateOrderStatus };
};
